import { ArrowUpCircle, Check, Loader2, Rocket } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/components/shared";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { microAppService } from "@/lib/services";
import { PublishAppDialog } from "@/pages/apps/microApps/PublishAppDialog";
import { useMicroAppUpdateTarget } from "./MicroAppUpdateContext";

interface PublishAppButtonProps {
  attachmentUuid: string | undefined;
  title: string;
}

/**
 * Publishes a generated HTML artifact as a first-class Micro App — or, when the
 * user arrived here via a micro app's "Edit in chat" deep-link, updates that
 * existing app's content instead of forking a new one.
 *
 * Fresh publishes open a dialog (name + description + per-source data mode);
 * the update flow stays one-click (it edits an existing app's content).
 */
export function PublishAppButton({ attachmentUuid, title }: Readonly<PublishAppButtonProps>) {
  const enabled = useFeatureFlag("microApps");
  const { showToast } = useToast();
  const { target, setTarget, registerPublishedApp } = useMicroAppUpdateTarget();
  const [state, setState] = useState<"idle" | "publishing" | "published">("idle");
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!enabled || !attachmentUuid) return null;

  const updating = target !== null;

  async function handleClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (state !== "idle" || !attachmentUuid) return;
    if (target) {
      // Update flow — replace the existing app's content directly.
      setState("publishing");
      try {
        await microAppService.republish(target.appUuid, attachmentUuid);
        registerPublishedApp(attachmentUuid, target.appUuid);
        setState("published");
        showToast(`Updated “${target.appName}”`, "success");
        setTarget(null);
      } catch {
        setState("idle");
        showToast("Failed to update micro app", "error");
      }
      return;
    }
    setDialogOpen(true);
  }

  const { icon: Icon, label, spin } = publishVisual(state, target?.appName);

  return (
    <>
      <button
        onClick={handleClick}
        disabled={state !== "idle"}
        className={`p-1.5 rounded transition disabled:opacity-60 ${
          updating
            ? "text-brand-primary hover:bg-brand-primary/10"
            : "text-content-tertiary hover:text-content-primary hover:bg-interactive-hover"
        }`}
        title={label}
        aria-label={label}
      >
        <Icon className={`w-3.5 h-3.5${spin ? " animate-spin" : ""}`} />
      </button>
      {dialogOpen && (
        <PublishAppDialog
          attachmentUuid={attachmentUuid}
          defaultName={title}
          onClose={() => setDialogOpen(false)}
          onPublished={(app) => {
            registerPublishedApp(attachmentUuid, app.uuid);
            setDialogOpen(false);
            setState("published");
          }}
        />
      )}
    </>
  );
}

function publishVisual(state: "idle" | "publishing" | "published", updateAppName?: string) {
  if (state === "publishing") return { icon: Loader2, label: updateAppName ? "Updating…" : "Publishing…", spin: true };
  if (state === "published") return { icon: Check, label: updateAppName ? "Updated" : "Published", spin: false };
  if (updateAppName) return { icon: ArrowUpCircle, label: `Update “${updateAppName}”`, spin: false };
  return { icon: Rocket, label: "Publish as app", spin: false };
}
